import { Controller } from '@hotwired/stimulus';
import { postGeneratorOpenAI, getPaywallController } from '../lib/generator_api';
import {
  bindFormulaDelimiterListener,
  delimiterPromptInstruction,
  processGeneratedFormula,
  unbindFormulaDelimiterListener,
} from '../lib/generator_formula_delimiter';

export default class extends Controller {
  static targets = ['usecase', 'cell', 'submit', 'submitLabel', 'output', 'error', 'successNote'];

  static values = {
    tool: { type: String, default: 'formulator' }
  };

  connect() {
    bindFormulaDelimiterListener(this);
    const params = new URLSearchParams(window.location.search);
    if (params.has('prompt') && this.hasUsecaseTarget) {
      this.usecaseTarget.focus({ preventScroll: true });
      document.getElementById('url-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  disconnect() {
    unbindFormulaDelimiterListener(this);
  }

  paywall() {
    return getPaywallController(this.element);
  }

  async copy(event) {
    event?.preventDefault();
    const formulaEl = this.outputTarget?.querySelector('p.font-mono');
    const text = (
      this.formulaValue ||
      formulaEl?.textContent ||
      this.outputTarget?.textContent ||
      ''
    )
      .replace(/^Copied to clipboard:\s*/i, '')
      .trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (this.hasSuccessNoteTarget) {
        this.successNoteTarget.textContent = 'Copied again.';
        this.successNoteTarget.classList.remove('hidden');
      }
    } catch {
      // Clipboard may be blocked
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearError();

    const usecase = this.usecaseTarget.value.trim();
    if (!usecase) {
      this.showError('Add a description first.');
      return;
    }

    const cellRaw = this.cellTarget.value;
    const cellValue = !cellRaw || cellRaw.trim() === '' ? 'A1' : cellRaw.trim();
    const userRole =
      'You are a helpful formula generator. You will help generate a Google Sheets formula to use in a google sheet. ';
    const prompt =
      'You will generate a formula to use in a google sheet. Use any existing Google Sheets formula. Do not include the explanation in your response. Reply with only the formula including the equals (=) sign that starts every formula. Then insert the formula and range appropriately.' +
      delimiterPromptInstruction() +
      ' ' +
      String.raw`
        Here are some examples: 
        Use Case: I need to create a red column chart. 
        Range to use: A1:A5
        Response: SPARKLINE(A1:A5, {"charttype","column"; "axis", true; "axiscolor", "red"})
        Use case: I want to make a bar chart with maximum 40
        Range to use: A2:E2 
        Response: SPARKLINE(A2:E2,{"charttype","bar";"max",40})
        Use Case: ` +
      usecase +
      `
          Range to use: ` +
      cellValue;

    this.setLoading(true);

    try {
      const result = await postGeneratorOpenAI({
        userRole,
        prompt,
        generator: this.toolValue
      });

      if (result.limited) {
        this.paywall()?.handleLimitReached();
        return;
      }

      if (result.error) {
        console.error('Formula generator proxy error', result.response?.status, result.detail);
        this.showError('Could not generate a formula. Try again in a moment.');
        return;
      }

      const data = result.data;
      const rawFormula = data.choices?.[0]?.message?.content?.trim();

      if (!rawFormula) {
        console.error('OpenAI proxy returned no formula', data);
        this.showError('No formula came back. Add more detail and try again.');
        return;
      }

      const { canonical, display } = processGeneratedFormula(rawFormula);
      this.canonicalFormula = canonical;
      this.formulaValue = display;

      try {
        await navigator.clipboard.writeText(display);
      } catch {
        // Safari can deny clipboard without a fresh user gesture.
      }
      this.paywall()?.applyUsageFromResponse(data.generator_usage);

      if (this.hasOutputTarget) {
        const formulaEl = this.outputTarget.querySelector('p.font-mono');
        if (formulaEl) {
          formulaEl.textContent = display;
        } else {
          this.outputTarget.textContent = display;
        }
        this.outputTarget.classList.remove('hidden');
      }
      if (this.hasSuccessNoteTarget) {
        this.successNoteTarget.textContent = 'Copied. Paste into your sheet.';
        this.successNoteTarget.classList.remove('hidden');
      }

      if (data.generator_usage?.remaining === 1) {
        this.showSuccessNudge();
      }
      if (data.generator_usage?.remaining === 0) {
        this.paywall()?.handleLimitReached();
      }
    } catch (err) {
      console.error('Formulator request failed', err);
      this.showError('Connection failed. Check your network and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden');
      this.errorTarget.textContent = '';
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) {
      const paywall = this.paywall();
      const atLimit = paywall && !paywall.paidMemberValue && paywall.remainingValue === 0;
      if (atLimit) {
        paywall.syncUi(0);
      } else {
        this.submitTarget.disabled = isLoading;
      }
    }
    if (this.hasSubmitLabelTarget) {
      this.submitLabelTarget.textContent = isLoading ? 'Generating…' : 'Generate';
    }
  }

  showSuccessNudge() {
    if (this.hasSuccessNoteTarget) {
      this.successNoteTarget.textContent = '1 free generation left on this tool.';
      this.successNoteTarget.classList.remove('hidden');
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove('hidden');
    }
  }
}
